// pages/Chat.jsx
import { useState, useRef, useEffect, useCallback } from "react";
import { T } from "../Tokens";
import { USER_ID, API_BASE, SUGGESTIONS } from "../config";
import { Chip, Dot, Skeleton, Divider } from "../components/UI";

// ── helpers ──────────────────────────────────────────────────────
const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

function hydrateMsgs(raw = []) {
  return raw.map((m, i) => {
    const time = m.ts
      ? new Date(m.ts).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";
    if (m.role === "user")
      return { id: `h-${i}`, role: "user", text: m.message || "", time };
    const p = m.payload;
    return {
      id: `h-${i}`,
      role: "assistant",
      text: p?.reply || m.message || "",
      intent: p?.intent || "CHAT",
      verification: p?.verification || null,
      time,
    };
  });
}

// ── VerificationCard ─────────────────────────────────────────────
function VerificationCard({ v, isChat }) {
  // Fix 1: scale 0–1 decimals to 0–100
  var sum = 0;
  v.citations?.forEach((c) => {
    sum +=
      c.fake_probability != null ? Math.round(c.fake_probability * 100) : 0;
  });
  const fp = Math.round(sum / (v.citations?.length || 1));
  const tp = 100 - fp;

  if (!v?.verdict || (tp === 0 && fp === 0)) return null;
  const verdict = v.verdict.toUpperCase();
  const vMap = { TRUE: "green", FALSE: "red", UNVERIFIED: "amber" };

  return (
    <div
      style={{
        display: isChat ? "none" : "block",
        marginTop: 10,
        background: T.bg1,
        borderRadius: T.radius,
        border: `1px solid ${T.border}`,
        overflow: "hidden",
        animation: "fadeUp 0.25s ease both",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg2,
        }}
      >
        <Chip variant={vMap[verdict] || "amber"}>{verdict}</Chip>
        <span style={{ fontSize: 10, color: T.textDim }}>
          Verification result
        </span>
      </div>

      {/* probability bars */}
      <div
        style={{
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", gap: 12 }}>
          {[
            { label: "Credibility", val: tp, color: T.green },
            { label: "Doubt", val: fp, color: T.red },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 10, color: T.textDim }}>{label}</span>
                <span
                  style={{
                    fontSize: 10,
                    color,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {val}%
                </span>
              </div>
              <div
                style={{
                  height: 3,
                  background: T.bg4,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: 3,
                    width: `${val}%`,
                    background: color,
                    borderRadius: 2,
                    transition: "width 0.6s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {v.reasoning && (
          <p
            style={{
              fontSize: 11,
              color: T.textSecondary,
              lineHeight: 1.6,
              margin: 0,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {v.reasoning}
          </p>
        )}
      </div>

      {/* citations */}
      {v.citations?.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          <div
            style={{
              fontSize: 9,
              color: T.textDim,
              padding: "8px 12px 5px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Sources ({v.citations.length})
          </div>
          {v.citations.map((c, i) => {
            const isObj = typeof c === "object" && c !== null;
            const url = isObj ? c.url : c;
            const title = isObj ? c.title : null;
            const desc = isObj ? c.description : null;
            const score = isObj ? c.score : null;
            // Fix 3: citation fake/real are also 0–1 decimals
            const fakeP = isObj
              ? Math.round((c.fake_probability || 0) * 100)
              : null;
            const realP = isObj
              ? Math.round((c.real_probability || 0) * 100)
              : null;

            return (
              <div
                key={i}
                style={{
                  margin: "0 8px 8px",
                  background: T.bg2,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "8px 10px",
                    borderBottom: isObj ? `1px solid ${T.border}` : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                      marginBottom: title ? 4 : 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        color: T.textDim,
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      #{i + 1}
                    </span>
                    {title ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: T.textPrimary,
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                        }}
                      >
                        {title}
                      </span>
                    ) : (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 11,
                          color: T.blue,
                          textDecoration: "none",
                          wordBreak: "break-all",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {url}
                      </a>
                    )}
                  </div>
                  {desc && (
                    <p
                      style={{
                        fontSize: 10,
                        color: T.textDim,
                        lineHeight: 1.5,
                        margin: "4px 0 0 15px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {desc}
                    </p>
                  )}
                </div>

                {isObj && (
                  <div
                    style={{
                      padding: "6px 10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                    }}
                  >
                    {/* score + stacked bars */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {score !== null && (
                        <span
                          style={{
                            fontSize: 9,
                            color: T.textDim,
                            background: T.bg3,
                            border: `1px solid ${T.border}`,
                            borderRadius: T.radiusSm,
                            padding: "1px 6px",
                            flexShrink: 0,
                          }}
                        >
                          score {(score * 100).toFixed(1)}%
                        </span>
                      )}

                      {fakeP !== null && (
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          {/* fake bar (red) — top */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                color: T.redText,
                                whiteSpace: "nowrap",
                                width: 42,
                              }}
                            >
                              fake {fakeP}%
                            </span>
                            <div
                              style={{
                                flex: 1,
                                height: 2,
                                background: T.bg4,
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: 2,
                                  width: `${fakeP}%`,
                                  background: T.red,
                                  borderRadius: 1,
                                }}
                              />
                            </div>
                          </div>

                          {/* real bar (green) — below */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                color: T.greenText,
                                whiteSpace: "nowrap",
                                width: 42,
                              }}
                            >
                              real {realP}%
                            </span>
                            <div
                              style={{
                                flex: 1,
                                height: 2,
                                background: T.bg4,
                                borderRadius: 1,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: 2,
                                  width: `${realP}%`,
                                  background: T.green,
                                  borderRadius: 1,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* url — below bars, left-aligned in blue */}
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 9,
                          color: T.blue,
                          textDecoration: "none",
                          wordBreak: "break-all",
                          overflowWrap: "anywhere",
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        ↗ <span>{url}</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── TypingIndicator ──────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div
      style={{
        display: "flex",
        gap: 5,
        padding: "10px 13px",
        background: T.bg2,
        borderRadius: T.radius,
        border: `1px solid ${T.border}`,
        width: "fit-content",
        alignItems: "center",
        animation: "fadeUp 0.2s ease both",
      }}
    >
      {[0, 0.2, 0.4].map((delay, i) => (
        <span
          key={i}
          style={{
            width: 4,
            height: 4,
            borderRadius: "50%",
            background: T.textDim,
            display: "block",
            animation: `blink 1.2s ${delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ── Message ──────────────────────────────────────────────────────
function Message({ msg, animate }) {
  const isUser = msg.role === "user";
  const intent = (msg.intent || "CHAT").toUpperCase();

  if (msg.isError)
    return (
      <div
        style={{
          alignSelf: "flex-start",
          maxWidth: "88%",
          animation: animate ? "fadeUp 0.2s ease both" : "none",
        }}
      >
        <Chip variant="red" style={{ marginBottom: 5, display: "inline-flex" }}>
          Error
        </Chip>
        <div
          style={{
            padding: "10px 13px",
            borderRadius: T.radius,
            borderTopLeftRadius: 2,
            fontSize: 12,
            lineHeight: 1.6,
            background: T.redDim,
            border: `1px solid ${T.redBorder}`,
            color: T.redText,
          }}
        >
          {msg.text}
        </div>
        <div style={{ fontSize: 9, color: T.textDim, marginTop: 4 }}>
          {msg.time}
        </div>
      </div>
    );

  return (
    <div
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        maxWidth: "88%",
        animation: animate ? "fadeUp 0.2s ease both" : "none",
      }}
    >
      {!isUser && (
        <Chip
          variant={intent === "VERIFY" ? "amber" : "blue"}
          style={{ marginBottom: 5 }}
        >
          {intent}
        </Chip>
      )}
      <div
        style={{
          padding: "10px 13px",
          fontSize: 12,
          lineHeight: 1.7,
          borderRadius: T.radius,
          wordBreak: "break-word",
          overflowWrap: "break-word",
          ...(isUser
            ? {
                borderBottomRightRadius: 2,
                background: T.greenDim,
                border: `1px solid ${T.greenBorder}`,
                color: T.greenText,
              }
            : {
                borderBottomLeftRadius: 2,
                background: T.bg2,
                border: `1px solid ${T.border}`,
                color: T.textPrimary,
              }),
        }}
      >
        {msg.text}
      </div>
      {!isUser && msg.verification && (
        <VerificationCard v={msg.verification} isChat={intent === "CHAT"} />
      )}
      <div style={{ fontSize: 9, color: T.textDim, marginTop: 4 }}>
        {msg.time}
      </div>
    </div>
  );
}

// ── EmptyState ───────────────────────────────────────────────────
function EmptyState({ onSend }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        paddingTop: 40,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: T.bg2,
          border: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill={T.textDim}>
          <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
        </svg>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 4 }}>
          Ask a question or verify a news claim
        </div>
        <div style={{ fontSize: 11, color: T.textDim }}>
          Try one of these to get started
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          justifyContent: "center",
          maxWidth: 460,
        }}
      >
        {SUGGESTIONS.map((s, i) => (
          <button key={i} className="suggestion-btn" onClick={() => onSend(s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── ChatInput ────────────────────────────────────────────────────
function ChatInput({ input, setInput, onSend, disabled }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled) inputRef.current?.focus();
  }, [disabled]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      style={{
        background: T.bg1,
        borderTop: `1px solid ${T.border}`,
        padding: "12px 16px",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div className="input-wrap">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={
              disabled
                ? "Loading session…"
                : "Ask a question or paste a claim to verify…"
            }
            disabled={disabled}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              outline: "none",
              fontSize: 12,
              color: T.textPrimary,
              fontFamily: T.fontMono,
            }}
          />
          {input && (
            <button
              onClick={() => setInput("")}
              style={{
                background: "none",
                border: "none",
                color: T.textDim,
                cursor: "pointer",
                fontSize: 16,
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
        <button
          className="send-btn"
          onClick={onSend}
          disabled={disabled || !input.trim()}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={T.greenText}>
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>
      <div style={{ marginTop: 6 }}>
        <span style={{ fontSize: 9, color: T.textDim }}>
          Enter to send · Shift+Enter for newline
        </span>
      </div>
    </div>
  );
}

// ── Chat Page ────────────────────────────────────────────────────
export default function Chat({ sessionId = "1" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historyCount, setHistory] = useState(0);
  const [newIds, setNewIds] = useState(new Set());
  const bottomRef = useRef(null);

  // Load session history
  useEffect(() => {
    setMessages([]);
    setInput("");
    setLoading(true);
    fetch(`${API_BASE}/sessions/${USER_ID}/${sessionId}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data) => {
        const h = hydrateMsgs(data);
        setMessages(h);
        setHistory(h.length);
        setLoading(false);
      })
      .catch(() => {
        setHistory(0);
        setLoading(false);
      });
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = useCallback(
    async (text) => {
      const msg = (text || input).trim();
      if (!msg || busy || loading) return;
      setInput("");
      setBusy(true);
      const uid = Date.now();
      setMessages((prev) => [
        ...prev,
        { role: "user", text: msg, time: nowTime(), id: uid },
      ]);
      setNewIds((prev) => new Set([...prev, uid]));

      try {
        const res = await fetch(`${API_BASE}/chat/${sessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: USER_ID, message: msg }),
        });
        const aid = Date.now() + 1;
        if (!res.ok) {
          const errText = await res.text().catch(() => `HTTP ${res.status}`);
          setMessages((p) => [
            ...p,
            {
              role: "assistant",
              isError: true,
              text: `Server error ${res.status}: ${errText.slice(0, 160)}`,
              time: nowTime(),
              id: aid,
            },
          ]);
          setNewIds((p) => new Set([...p, aid]));
        } else {
          const data = await res.json();
          console.log("Received response:", data);
          setMessages((p) => [
            ...p,
            {
              role: "assistant",
              text: data.reply || "",
              intent: data.intent,
              verification: data.verification,
              time: nowTime(),
              id: aid,
            },
          ]);
          setNewIds((p) => new Set([...p, aid]));
        }
      } catch {
        const eid = Date.now() + 2;
        setMessages((p) => [
          ...p,
          {
            role: "assistant",
            isError: true,
            text: "Could not reach server. Make sure Spring Boot is running on localhost:8080.",
            time: nowTime(),
            id: eid,
          },
        ]);
        setNewIds((p) => new Set([...p, eid]));
      } finally {
        setBusy(false);
      }
    },
    [input, busy, loading, sessionId],
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Page header */}
      <div
        style={{
          background: T.bg1,
          borderBottom: `1px solid ${T.border}`,
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: T.radius,
            background: T.redDim,
            border: `1px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill={T.red}>
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.textPrimary }}>
            News Agent
          </div>
          <div style={{ fontSize: 10, color: T.textDim }}>
            session {sessionId}
          </div>
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {loading && <Chip variant="amber">loading</Chip>}
          <Dot active={!loading} />
        </div>
      </div>

      {/* Messages area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <div
            style={{
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {[
              { a: "flex-end", w: "55%" },
              { a: "flex-start", w: "68%" },
              { a: "flex-end", w: "40%" },
              { a: "flex-start", w: "75%" },
            ].map((s, i) => (
              <div key={i} style={{ alignSelf: s.a, width: s.w }}>
                <Skeleton h={40} style={{ borderRadius: T.radius }} />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              flex: 1,
            }}
          >
            {messages.length === 0 && !busy && <EmptyState onSend={send} />}
            {historyCount > 0 && (
              <Divider label={`${historyCount} previous messages`} />
            )}
            {messages.map((msg) => (
              <Message key={msg.id} msg={msg} animate={newIds.has(msg.id)} />
            ))}
            {busy && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <ChatInput
        input={input}
        setInput={setInput}
        onSend={send}
        disabled={busy || loading}
      />
    </div>
  );
}
